import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';

const DEFAULT_LIMIT = 50;

const PROVIDER_COLORS = {
  github:    { bg: '#238636', text: '#fff' },
  gitlab:    { bg: '#e24329', text: '#fff' },
  bitbucket: { bg: '#0052cc', text: '#fff' },
  gitea:     { bg: '#609926', text: '#fff' },
};

function ProviderBadge({ provider }) {
  const colors = PROVIDER_COLORS[provider] || { bg: 'var(--border)', text: 'var(--text-primary)' };
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 999,
      background: colors.bg, color: colors.text,
      textTransform: 'capitalize', letterSpacing: '0.03em',
    }}>
      {provider}
    </span>
  );
}

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  );
}

export default function RepositoriesList() {
  const navigate = useNavigate();

  const [searchValue, setSearchValue]   = useState('');
  const [activeQ, setActiveQ]           = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  // '' = any, 'none' = repos with no product yet, otherwise a product id.
  const [productFilter, setProductFilter] = useState('');
  const [products, setProducts]         = useState([]);
  const [rows, setRows]                 = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [pages, setPages]               = useState(1);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  const gridRef = useRef();

  const fetchData = useCallback(async (q, provFilter, pageNum, prodFilter = '') => {
    setLoading(true);
    setError('');
    try {
      const params = { page: pageNum, limit: DEFAULT_LIMIT };
      if (q)          params.q        = q;
      if (provFilter) params.provider = provFilter;
      if (prodFilter === 'none')  params.unmapped   = 1;
      else if (prodFilter)        params.product_id = prodFilter;
      const res = await axios.get('/repositories', { params });
      setRows(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load repositories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData('', '', 1); }, [fetchData]);

  useEffect(() => {
    axios.get('/products', { params: { limit: 500 } })
      .then(res => setProducts(res.data.data || []))
      .catch(() => {});
  }, []);

  // Assigning a product here is the same decision the import wizard asks for
  // the first time a repo turns up; this is where it is corrected afterwards.
  function handleProductChange(repo, value) {
    const productId = value ? parseInt(value, 10) : null;
    const previous  = rows;
    setRows(rs => rs.map(r => r.id === repo.id
      ? { ...r, product_id: productId,
          product_name: products.find(p => p.id === productId)?.name || null }
      : r));
    axios.put(`/repositories/${repo.id}/product`, { product_id: productId })
      .catch(e => {
        setRows(previous);
        alert(e.response?.data?.error || 'Could not change the product for this repository');
      });
  }

  function handleSearch() {
    const q = searchValue.trim();
    setActiveQ(q);
    setPage(1);
    fetchData(q, providerFilter, 1, productFilter);
  }

  function handleClear() {
    setSearchValue('');
    setActiveQ('');
    setPage(1);
    fetchData('', providerFilter, 1, productFilter);
  }

  function handleProviderChange(e) {
    const val = e.target.value;
    setProviderFilter(val);
    setPage(1);
    fetchData(activeQ, val, 1, productFilter);
  }

  function handleProductFilterChange(e) {
    const val = e.target.value;
    setProductFilter(val);
    setPage(1);
    fetchData(activeQ, providerFilter, 1, val);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSearch();
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    fetchData(activeQ, providerFilter, newPage, productFilter);
  }

  function handleDelete(id, name) {
    if (!window.confirm(`Remove repository "${name}" from the system?`)) return;
    axios.delete(`/repositories/${id}`)
      .then(() => fetchData(activeQ, providerFilter, page, productFilter))
      .catch(e => alert(e.response?.data?.error || 'Delete failed'));
  }

  const columnDefs = [
    {
      headerName: 'Provider',
      field: 'provider',
      width: 120,
      sortable: true,
      cellRenderer: params => <ProviderBadge provider={params.value} />,
    },
    {
      headerName: 'Repository',
      field: 'repo_name',
      flex: 2,
      minWidth: 220,
      sortable: true,
      cellRenderer: params => (
        <a
          href={params.data.repo_full_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
        >
          {params.value}
        </a>
      ),
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 3,
      minWidth: 200,
      sortable: false,
      valueFormatter: p => p.value || '—',
      cellStyle: { color: 'var(--text-secondary)', fontSize: 13 },
    },
    {
      headerName: 'Product',
      field: 'product_id',
      width: 240,
      sortable: false,
      cellRenderer: params => (
        <select
          className="form-input"
          style={{ margin: 0, fontSize: 12, height: 30, padding: '2px 6px', width: '100%' }}
          value={params.data.product_id || ''}
          onChange={e => handleProductChange(params.data, e.target.value)}
        >
          <option value="">— not mapped —</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      ),
    },
    {
      headerName: 'Owner',
      field: 'owner',
      width: 200,
      sortable: true,
      valueFormatter: p => p.value || '—',
      cellStyle: params => ({
        color: params.value ? 'var(--text-primary)' : 'var(--text-muted)',
        fontStyle: params.value ? 'normal' : 'italic',
      }),
    },
    {
      headerName: 'Last Synced',
      field: 'last_synced_at',
      width: 150,
      valueFormatter: p => p.value ? new Date(p.value).toLocaleDateString() : '—',
      cellStyle: { color: 'var(--text-muted)', fontSize: 12 },
    },
    {
      headerName: '',
      width: 80,
      sortable: false,
      cellRenderer: params => (
        <button
          className="btn btn-ghost"
          style={{ fontSize: 12, color: 'var(--danger)' }}
          onClick={() => handleDelete(params.data.id, params.data.repo_name)}
        >
          <IconTrash />
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Repositories</h2>
        <p>
          All repositories synced from GitHub, GitLab, Bitbucket, and Gitea. A repository belongs
          to one product; features imported from its PRs land there.
        </p>
      </div>

      <div className="page-body">
        <div className="toolbar">
          <select
            className="form-input"
            style={{ width: 140 }}
            value={providerFilter}
            onChange={handleProviderChange}
          >
            <option value="">All providers</option>
            <option value="github">GitHub</option>
            <option value="gitlab">GitLab</option>
            <option value="gitea">Gitea</option>
            <option value="bitbucket">Bitbucket</option>
          </select>

          <select
            className="form-input"
            style={{ width: 200 }}
            value={productFilter}
            onChange={handleProductFilterChange}
          >
            <option value="">All products</option>
            <option value="none">Not mapped yet</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          <input
            className="form-input search-input"
            placeholder="Search by repo name…"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button className="btn btn-secondary" onClick={handleSearch}>
            <IconSearch /> Search
          </button>
          {(activeQ || providerFilter || productFilter) && (
            <button
              className="btn btn-ghost"
              style={{ color: 'var(--danger)' }}
              onClick={() => {
                setSearchValue('');
                setActiveQ('');
                setProviderFilter('');
                setProductFilter('');
                setPage(1);
                fetchData('', '', 1);
              }}
            >
              Clear
            </button>
          )}

          <div className="spacer" />

          <button
            className="btn btn-secondary"
            onClick={() => fetchData(activeQ, providerFilter, page, productFilter)}
            disabled={loading}
          >
            <IconRefresh /> Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/repositories/providers')}
          >
            <IconSettings /> Provider Config
          </button>
        </div>

        {(activeQ || providerFilter || productFilter) && (
          <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
            {[
              providerFilter && `Provider: ${providerFilter}`,
              activeQ && `Name: "${activeQ}"`,
              productFilter === 'none'
                ? 'Product: not mapped yet'
                : productFilter && `Product: ${products.find(p => String(p.id) === String(productFilter))?.name || productFilter}`,
            ].filter(Boolean).join(' · ')}
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        <div style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(15,23,42,0.5)', borderRadius: 'var(--radius-lg)',
            }}>
              <div className="spinner" />
            </div>
          )}
          <div
            className="ag-theme-alpine-dark"
            style={{ height: 520, width: '100%', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              suppressRowClickSelection
              suppressMovableColumns
              suppressCellFocus
              rowHeight={44}
              headerHeight={40}
              defaultColDef={{ sortable: true, resizable: true }}
              noRowsOverlayComponent={() => (
                <div className="empty-state">
                  <div className="icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                    </svg>
                  </div>
                  <h3>No repositories found</h3>
                  <p>
                    {activeQ || providerFilter || productFilter
                      ? 'Try clearing your filters'
                      : 'Configure a provider and run the Repository Sync job to populate this list'}
                  </p>
                  {!activeQ && !providerFilter && !productFilter && (
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: 12 }}
                      onClick={() => navigate('/repositories/providers')}
                    >
                      Configure Providers
                    </button>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 16, fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <span>
            {total === 0
              ? 'No results'
              : `${(page - 1) * DEFAULT_LIMIT + 1}–${Math.min(page * DEFAULT_LIMIT, total)} of ${total.toLocaleString()} repositor${total !== 1 ? 'ies' : 'y'}`}
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '5px 10px' }}
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              <IconChevronLeft />
            </button>
            <span style={{ minWidth: 80, textAlign: 'center' }}>
              Page {page} of {pages}
            </span>
            <button
              className="btn btn-secondary"
              style={{ padding: '5px 10px' }}
              disabled={page >= pages}
              onClick={() => handlePageChange(page + 1)}
            >
              <IconChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
